import {
  Camera,
  CameraErrorCode,
  EncodingType,
  MediaType,
} from "@capacitor/camera";
import { Filesystem } from "@capacitor/filesystem";

export type ScanClientDependencies = {
  camera?: Pick<typeof Camera, "takePhoto">;
  filesystem?: Pick<typeof Filesystem, "readFile">;
  now?: () => number;
};

export const createScanClient = (
  dependencies: ScanClientDependencies = {}
) => {
  const camera = dependencies.camera || Camera;
  const filesystem = dependencies.filesystem || Filesystem;
  const now = dependencies.now || Date.now;

  return {
    capture: async (): Promise<File | undefined> => {
      // The deprecated getPhoto prompt dynamically registers an Android
      // ActivityResult callback and can crash if a pending result is delivered
      // before the launcher field is assigned. Scan is the camera action; the
      // adjacent Import action remains the gallery/file path.
      const image = await camera
        .takePhoto({
          quality: 70,
          editable: "no",
          encodingType: EncodingType.JPEG,
        })
        .catch((error: unknown) => {
          // Closing the camera is a choice, not a failure.
          if (
            (error as { code?: unknown })?.code ===
            CameraErrorCode.TakePhotoCancelled
          ) {
            return undefined;
          }
          throw error;
        });
      if (!image || image.type !== MediaType.Photo || !image.uri) {
        return undefined;
      }

      const result = await filesystem.readFile({ path: image.uri });
      if (typeof result.data !== "string" || !result.data) return undefined;

      const bytes = Uint8Array.from(atob(result.data), (character) =>
        character.charCodeAt(0)
      );
      return new File([bytes], `Scan-${now()}.jpg`, { type: "image/jpeg" });
    },
  };
};

export type ScanClient = ReturnType<typeof createScanClient>;
